/**
 * CANONICAL partner-scoped usage schema — the single source of truth for the
 * V2→V3 usage migration. Both sides of the contract read this file:
 *   - the client (TeamUsageState, UsageMetricsService) imports these types;
 *   - `scripts/sync-v2-usage.ts` re-declares them structurally and cites this
 *     module by path. Change the shape here first, then the script.
 *
 * ── Why two disjoint counter sets ────────────────────────────────────────
 * V2 remains live and is still written daily, so the mirror cannot be a
 * one-shot copy — it is re-synced continuously until V2 is retired. That
 * forces one rule: **`v2Mirror` and `v3Native` must never overlap.**
 *
 *   `v2Mirror`  — ABSOLUTE lifetime totals, recomputed from the source
 *                 `domains/**` docs on EVERY sync and written with overwrite
 *                 semantics. Idempotent by construction: re-running the sync
 *                 any number of times converges on the same value, and any
 *                 client tampering self-heals on the next run.
 *   `v3Native`  — counters accrued by V3 compat surfaces (UsageMetricsService),
 *                 applied as INCREMENTS. The sync never reads or writes them.
 *
 * Display sums the two (`totalUsageCounters`). When V2 is finally retired the
 * sync simply stops: `v2Mirror` freezes as the historical baseline and
 * `v3Native` carries on. No cutover write, no double count, no data loss.
 *
 * ── Why partner-scoped ───────────────────────────────────────────────────
 * V2 keys usage by email domain. That is wrong for V3 in two ways: a district
 * whose staff span several email domains only ever saw one domain's numbers,
 * and the legacy `domains/{domain}/**` rule grants read AND write to any
 * signed-in user regardless of domain (firestore.rules — `isUsersOwnDomain`
 * returns a bare `isLoggedIn()`). Partner scoping fixes both: several source
 * domains fold into one partner via the `partner-id-map.json` ledger, and
 * every path below is gated by `isMember(partnerId)`.
 *
 * @security FERPA — staff usage counters only, never student records. Event
 *                   docs carry staff uid/email; never log them.
 * @feature admin
 */

/** Usage-counter map — event code → count. */
export type UsageStatsMap = Record<string, number>;

/** Firestore path helpers — the only place these strings are constructed. */
export const usagePaths = {
  /** Aggregate summary doc: `partners/{partnerId}/usage/summary`. */
  summary: (partnerId: string) => `partners/${partnerId}/usage/summary`,
  /** Per-user counters collection: `partners/{partnerId}/usage_users`. */
  users: (partnerId: string) => `partners/${partnerId}/usage_users`,
  /** Per-user counters doc, keyed by auth uid (shared V2/V3 Auth project). */
  user: (partnerId: string, uid: string) => `partners/${partnerId}/usage_users/${uid}`,
  /** Event stream collection: `partners/{partnerId}/usage_events`. */
  events: (partnerId: string) => `partners/${partnerId}/usage_events`,
} as const;

/**
 * `partners/{partnerId}/usage/summary` — one per account.
 *
 * Written by the sync (mirror + provenance + watermarks) and by
 * UsageMetricsService (native increments only). The two writers touch
 * disjoint fields, so neither clobbers the other.
 */
export interface UsageSummaryDoc {
  /** Absolute V2 totals, summed across every contributing source domain. */
  v2Mirror?: UsageStatsMap;
  /** Counters accrued natively by V3 compat surfaces. */
  v3Native?: UsageStatsMap;
  /** Union of `userTags` across contributing domains — the filter vocabulary. */
  userTags?: string[];
  /** Provenance: which V2 email domains fold into this partner. */
  sourceDomains?: string[];
  /**
   * Per-domain high-water mark (max event `timestamp` pulled). The sync
   * resumes from these, so a re-run costs only the delta. Keyed by domain;
   * the boundary is inclusive (`>=`) and deduped by deterministic event id,
   * so equal-millisecond events at the edge can never be skipped.
   */
  domainWatermarks?: Record<string, number>;
  /** ms epoch of the last completed sync run. */
  lastSyncedAt?: number;
}

/** `partners/{partnerId}/usage_users/{uid}` — per-user counters. */
export interface UsageUserDoc {
  /** Firestore doc id — the auth uid. */
  id?: string;
  uid?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  /** Free-form team tags carried over from V2 (drives the tag filter). */
  tags?: string[];
  /** Absolute V2 per-user totals (summed if the uid appears in >1 domain). */
  v2Mirror?: UsageStatsMap;
  /** Per-user counters accrued natively by V3 compat surfaces. */
  v3Native?: UsageStatsMap;
  /** Provenance: source domains this row's mirror was built from. */
  sourceDomains?: string[];
  lastSyncedAt?: number;
}

/**
 * `partners/{partnerId}/usage_events/{eventId}` — the timeline's event stream.
 *
 * Doc ids are deterministic for mirrored rows (`v2_{domain}_{sourceDocId}`)
 * so re-syncing an already-copied event is a no-op rather than a duplicate.
 * Native rows use auto-ids and can never collide with the `v2_` prefix.
 */
export interface UsageEventDoc {
  id?: string;
  eventName?: string;
  code?: string;
  uid?: string;
  email?: string;
  /** ms epoch. */
  timestamp?: number;
  /** Which system recorded it. */
  source?: 'v2' | 'v3';
  /** Mirrored rows only — the V2 domain it came from. */
  sourceDomain?: string;
}

/** Deterministic mirrored-event doc id. Keep in lockstep with the sync script. */
export function mirroredEventId(domain: string, sourceDocId: string): string {
  return `v2_${domain}_${sourceDocId}`;
}

/**
 * Sum the mirrored and native counter sets for display. The two are disjoint
 * by construction (see module JSDoc), so a plain add is correct — never
 * double counts, and stays right after V2 is retired.
 */
export function totalUsageCounters(
  v2Mirror: UsageStatsMap | undefined,
  v3Native: UsageStatsMap | undefined,
): UsageStatsMap {
  const total: UsageStatsMap = { ...(v2Mirror ?? {}) };
  for (const [code, value] of Object.entries(v3Native ?? {})) {
    total[code] = (total[code] ?? 0) + value;
  }
  return total;
}
